import { logger } from '@/lib/logger';
// Module has types and real JS objects as same name, so using 2 imports
import type {
  ClientInteraction,
  ErrorReason,
  Status,
} from '@paypalcorp/ppclientinteractions';
import type { CustomerInteraction, CustomerInteractionType } from './constants';
import { CUSTOMER_INTERACTION } from './constants';

// Temporary until module exports a way to work with parents
type InteractionEvent = { interactionName: string };

// Pull some types out of module
type ClientInteractionMethodName = keyof ClientInteraction;
type GetCurrentClientInteractionEventResponse = NonNullable<
  ReturnType<ClientInteraction['getCurrentClientInteractionEvent']>
>;

type SetCurrentInteractionMetadataReturnType =
  | ReturnType<GetCurrentClientInteractionEventResponse['setMetadata']>
  | undefined;

type MaybeReturnClientInteractionMethod = {
  [Property in keyof ClientInteraction]: (
    ...params: Parameters<ClientInteraction[Property]>
  ) => ReturnType<ClientInteraction[Property]> | void;
};

// `resume` is a new method that was added for "cross-border" interactions use cases
// Omitting it now for Weasley until we need it.
interface CI extends Omit<MaybeReturnClientInteractionMethod, 'resume'> {
  Event: CustomerInteractionType;
  Status: typeof Status;
  /** Set the current Customer Interaction in module to status Ok */
  ok: () => SetCurrentInteractionMetadataReturnType;
  /** Set the current Customer Interaction in module to status FCI */
  fci: (errorReason?: ErrorReason) => SetCurrentInteractionMetadataReturnType;
  /** Set the current Customer Interaction in module to status Flag for PayPal policy errors */
  flag: () => SetCurrentInteractionMetadataReturnType;
  /** Set the current Customer Interaction in module to status Error */
  error: (error: ErrorReason) => SetCurrentInteractionMetadataReturnType;
  /** User chose to cancel the current CI */
  userCanceled: () => void;
  /** Ends the current CI (last CI in stack) */
  endCurrent: (status?: Status, error?: ErrorReason) => void;
  ErrorReason: typeof ErrorReason;
  /**
   * Gets the parent event of a child event
   * If there is no parent returns the child
   */
  getParent: (childEventName: CustomerInteraction) => CustomerInteraction;
}

// We only want to ever log once that it is not initialized
let hasLoggedNotInitialized = false;

/**
 * Creates a function that safely executes a method on an object
 * * When Proxy is adopted enough we can replace this
 * @param methodName The method to execute
 * @returns The output of the method or undefined if an error occurs
 */
export function buildSafeMethod<MethodName extends ClientInteractionMethodName>(
  methodName: MethodName,
) {
  return (
    ...args: Parameters<ClientInteraction[MethodName]>
  ): ReturnType<ClientInteraction[MethodName]> | void => {
    try {
      // @ts-expect-error
      return window.PPClientInteractions.interaction[methodName](...args);
    } catch (err) {
      // Catch and log any errors in module code
      const { message, name, stack } =
        err instanceof Error ? err : new Error(err?.toString());

      if (!window.PPClientInteractions?.interaction?.isInitialized?.()) {
        if (!hasLoggedNotInitialized) {
          hasLoggedNotInitialized = true;
          logger.info('CI_MODULE_NOT_INITIALIZED', {
            existsOnWindow: Boolean(window.PPClientInteractions),
          });
        }
      } else {
        logger.error(`ERROR_CALLING_CI_METHOD_${methodName.toUpperCase()}`, {
          args,
          message,
          name,
          stack,
        });
      }
    }
  };
}

// Module doesn't export this on it's own right now
// TODO: Delete once module exports them
const CI_STATUS: typeof Status = {
  Cancel: 'Cancel',
  Error: 'Error',
  FCI: 'FCI',
  Ok: 'Ok',
  Start: 'Start',
};
const CI_ERROR_REASONS: typeof ErrorReason = {
  Application: 'Application',
  Auth: 'Auth',
  Flag: 'Flag',
  Network: 'Network',
  None: 'None',
  Server: 'Server',
  ThirdParty: 'ThirdParty',
  User: 'User',
};

// When updating PPClientInteractions module you may have to add/remove methods here
// Provides a safe way to call and short method path for PPClientInteractions.interactions
export const CI: CI = {
  end: buildSafeMethod('end'),
  endAll: buildSafeMethod('endAll'),
  endCurrent: (status, error) => {
    const currentCi =
      CI.getCurrentClientInteractionEvent()?.getMetadata().interaction;
    if (currentCi) {
      if (status) {
        CI.endWith(currentCi, status, error);
      } else {
        CI.end(currentCi);
      }
    } else {
      logger.warn('ATTEMPTED_END_ON_EMPTY_CI_STACK');
    }
  },
  endWith: buildSafeMethod('endWith'),
  error: error =>
    CI.getCurrentClientInteractionEvent()?.setMetadata?.(
      CI.Status.Error,
      error,
    ),
  ErrorReason: CI_ERROR_REASONS,
  Event: CUSTOMER_INTERACTION,
  fci: errorReason =>
    CI.getCurrentClientInteractionEvent()?.setMetadata?.(
      CI.Status.FCI,
      errorReason,
    ),
  flag: () =>
    CI.getCurrentClientInteractionEvent()?.setMetadata(
      CI.Status.Error,
      CI.ErrorReason.Flag,
    ),
  getClientInteractionEvent: buildSafeMethod('getClientInteractionEvent'),
  getCurrentClientInteractionEvent: buildSafeMethod(
    'getCurrentClientInteractionEvent',
  ),

  // The module does not yet expose a way todo this, so we reach into it until it does
  getParent: childEventName => {
    const allInteractions = window.PPClientInteractions?.interaction // @ts-expect-error
      .interactions as InteractionEvent[];
    const childIndex = allInteractions.findIndex(
      event => event.interactionName === childEventName,
    );

    // Child must exist, not be first item (0 index)
    if (childIndex && allInteractions.length > 1) {
      return allInteractions[childIndex - 1]
        ?.interactionName as CustomerInteraction;
    }

    return childEventName;
  },
  isInitialized: buildSafeMethod('isInitialized'),
  ok: () => CI.getCurrentClientInteractionEvent()?.setMetadata?.(CI.Status.Ok),
  setLogger: buildSafeMethod('setLogger'),
  setMetadata: buildSafeMethod('setMetadata'),
  start: buildSafeMethod('start'),
  Status: CI_STATUS,
  updateMetadata: buildSafeMethod('updateMetadata'),
  userCanceled: () => CI.endCurrent(CI.Status.Cancel, CI.ErrorReason.User),
};
